import {
    Avatar,
    Button,
    Card,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Text,
    Tooltip,
    makeStyles,
    shorthands,
} from '@fluentui/react-components';
import { MoreVerticalFilled } from '@fluentui/react-icons';
import React, { useMemo, useState } from 'react';
import { NexusAgent } from '../../../../domain/agents/models/Agent';
import { ReactComponent as Pinned } from '../../../../assets/Pinned.svg';
import { ReactComponent as Unpinned } from '../../../../assets/Unpin.svg';
import { buildAvatarSrc } from '../../../utils/AvatarUtils';
import { customColors, customFonts } from '../../../../styles';
import { usePinnedAgents } from '../../../ChatList/hooks/useRecentlyUsedAgents';

const useStyles = makeStyles({
    card: {
        width: '350px',
        height: '120px',
        ...shorthands.margin('10px'),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'start',
        backgroundColor: '#F3F6F8',
        ...shorthands.borderLeft('5px', 'solid', customColors.newChatHoverLightBlue),
        ...shorthands.borderTop('1px', 'solid', 'transparent'),
        ...shorthands.borderRight('1px', 'solid', 'transparent'),
        ...shorthands.borderBottom('1px', 'solid', 'transparent'),
        ...shorthands.transition('backgroundColor', '0.3s', 'ease-in-out'),
        position: 'relative',
        boxSizing: 'border-box',
        '&:hover': {
            backgroundColor: customColors.white,
            ...shorthands.borderLeft('5px', 'solid', customColors.protivitiOrange),
            ...shorthands.borderRight('1px', 'solid', customColors.protivitiOrange),
            ...shorthands.borderBottom('1px', 'solid', customColors.protivitiOrange),
            ...shorthands.borderTop('1px', 'solid', customColors.protivitiOrange),
            boxShadow: '5px 5px 15px rgba(0, 0, 0, 0.2)',
            cursor: 'pointer',
        },
        '&:disabled': {
            opacity: 0.5,
            pointerEvents: 'none',
            cursor: 'not-allowed',
        },
        '@media (max-width: 576px)': {
            width: '315px',
            height: '135px',
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        marginBottom: '10px',
        textOverflow: 'ellipsis',
        height: '120px',
        maxHeight: '120px',
        ...shorthands.padding('0', '10px', '0', '10px'),
    },
    image: {
        width: '72px',
        height: '72px',
        objectFit: 'cover',
        marginBottom: '10px',
        marginRight: '10px',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        ...shorthands.border('2px', 'solid', customColors.newChatHoverLightBlue),
        flexShrink: 0,
    },
    textContent: {
        marginLeft: '10px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'end',
        alignContent: 'start',
        minWidth: 0,
    },
    headerText: {
        display: '-webkit-box',
        '-webkit-line-clamp': '1',
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
        textAlign: 'left',
        fontWeight: '600',
        fontSize: '20px',
        fontFamily: customFonts.TitleFont,
        color: '#0a5782 !important',
        lineHeight: '25px',
        marginBottom: '5px',
        maxWidth: '200px',
        '@media screen and (max-width: 1370px)': {
            maxWidth: '190px',
        },
    },
    descriptionText: {
        display: '-webkit-box',
        '-webkit-line-clamp': '4',
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
        fontWeight: '400',
        fontSize: '12px',
        lineHeight: '1rem',
        textAlign: 'left',
        color: '#696969 !important',
        fontFamily: customFonts.Lato,
    },
    moreButton: {
        position: 'absolute',
        top: '10px',
        right: '5px',
        ...shorthands.border('none'),
        backgroundColor: 'transparent',
        ...shorthands.padding('5px', '10px'),
    },
    pinIcon: {
        width: '16px',
        height: '16px',
        '& path': {
            fill: 'currentColor',
        },
    },
    footer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        ...shorthands.padding('0', '16px', '14px', '16px'),
        gap: '12px',
    },
    providerLabel: {
        fontFamily: customFonts.Barlow,
        fontSize: '12px',
        fontWeight: '600',
        color: customColors.darkNavy,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
    },
    actionButton: {
        ...shorthands.borderRadius('0px'),
        ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
        backgroundColor: customColors.white,
        color: customColors.protivitiDarkBlue,
        fontFamily: customFonts.Lato,
        fontWeight: '600',
        '&:hover': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
        },
    },
});

export interface AgentTileProps {
    agent: NexusAgent;
    isDisabled?: boolean;
    onOpenChat: (agent: NexusAgent) => Promise<void>;
    onLearnMore: (agent: NexusAgent) => void;
}

const AgentTile: React.FC<AgentTileProps> = ({ agent, isDisabled = false, onOpenChat, onLearnMore }) => {
    const styles = useStyles();
    const [isOpeningChat, setIsOpeningChat] = useState(false);
    const { isPinned, pinnedAgentIds, togglePin, maxPinnedAgents } = usePinnedAgents();

    const avatarSrc = useMemo(
        () => buildAvatarSrc(agent.providerType, agent.agentIconUrl),
        [agent.agentIconUrl, agent.providerType],
    );

    const description = agent.shortDescription ?? agent.description;
    const isBusy = isDisabled || isOpeningChat;
    const pinned = isPinned(agent.agentId);
    const isPinLimitReached = !pinned && pinnedAgentIds.length >= maxPinnedAgents;
    const pinLimitMessage = `You can only have ${maxPinnedAgents} agents pinned.`;

    const handleOpenChat = async (): Promise<void> => {
        if (isBusy) {
            return;
        }

        setIsOpeningChat(true);

        try {
            await onOpenChat(agent);
        } finally {
            setIsOpeningChat(false);
        }
    };

    return (
        <Tooltip
            content="Max chat sessions reached. Please remove a chat to create a new one."
            relationship="label"
            visible={isDisabled ? undefined : false}
        >
            <Card
                className={styles.card}
                disabled={isBusy}
                onClick={() => {
                    void handleOpenChat();
                }}
                style={{
                    cursor: isOpeningChat ? 'progress' : isDisabled ? 'not-allowed' : 'pointer',
                }}
            >
                <div className={styles.content}>
                    <Avatar className={styles.image} aria-label={`${agent.name} avatar`} image={{ src: avatarSrc }} />

                    <div className={styles.textContent}>
                        <Text className={styles.headerText}>{agent.name}</Text>
                        <Text className={styles.descriptionText} truncate wrap={true}>
                            {description}
                        </Text>
                    </div>

                    <Menu>
                        <MenuTrigger>
                            <Button
                                appearance="transparent"
                                icon={<MoreVerticalFilled />}
                                aria-label={`More options for ${agent.name}`}
                                className={styles.moreButton}
                                onClick={(event) => event.stopPropagation()}
                            />
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onLearnMore(agent);
                                    }}
                                >
                                    Learn More
                                </MenuItem>
                                <MenuItem
                                    disabled={isPinLimitReached}
                                    title={isPinLimitReached ? pinLimitMessage : undefined}
                                    onClick={(event) => {
                                        event.stopPropagation();

                                        if (isPinLimitReached) {
                                            return;
                                        }

                                        togglePin(agent.agentId);
                                    }}
                                >
                                    {pinned ? 'Unpin Agent' : 'Pin Agent'}{' '}
                                    {pinned ? (
                                        <Pinned className={styles.pinIcon} />
                                    ) : (
                                        <Unpinned className={styles.pinIcon} />
                                    )}
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </div>
            </Card>
        </Tooltip>
    );
};

export default AgentTile;
